import React, { useState, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const broadcastBadge = {
  idle:  'bg-blue-50 text-blue-700 border-blue-200',
  live:  'bg-rose-50 text-rose-700 border-rose-200',
  ended: 'bg-gray-50 text-gray-600 border-gray-200',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

export default function SuperadminInstructorsShow({ instructor, assignments = [], available_webinars = [] }) {
  const [query, setQuery] = useState('');
  const [submittingId, setSubmittingId] = useState(null);
  const [assigningWebinarId, setAssigningWebinarId] = useState(null);
  const [loginCopied, setLoginCopied] = useState(false);

  const handleCopyLogin = async () => {
    try {
      await navigator.clipboard.writeText(instructor.login_url);
      setLoginCopied(true);
      setTimeout(() => setLoginCopied(false), 1500);
    } catch (_) {}
  };

  const handleRegenerate = () => {
    const ok = window.confirm(
      `Regenerate ${instructor.name}'s login link?\n\nThe current link will stop working immediately. You'll need to share the new one with the instructor.`
    );
    if (!ok) return;
    router.post(`/superadmin/instructors/${instructor.id}/regenerate_token`);
  };

  const filteredWebinars = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available_webinars;
    return available_webinars.filter((w) =>
      [w.title, w.status, w.delivery_mode].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [query, available_webinars]);

  const handleAssign = (webinar) => {
    setAssigningWebinarId(webinar.id);
    router.post(`/superadmin/instructors/${instructor.id}/assignments`,
      { webinar_id: webinar.id },
      { onFinish: () => setAssigningWebinarId(null) }
    );
  };

  const handleRemove = (assignment) => {
    const ok = window.confirm(`Remove "${assignment.webinar.title}" from ${instructor.name}?`);
    if (!ok) return;
    setSubmittingId(assignment.id);
    router.delete(`/superadmin/instructors/${instructor.id}/assignments/${assignment.id}`, {
      onFinish: () => setSubmittingId(null),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link
            href="/superadmin/instructors"
            className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            All instructors
          </Link>
          <div className="mt-3 flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{instructor.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{instructor.email}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Instructor login link</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Share this personal magic link with the instructor. It logs them into their portal where they can see every webinar they've been assigned to host.
          </p>
          {instructor.login_url ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={instructor.login_url}
                onFocus={(e) => e.target.select()}
                className="flex-1 font-mono text-xs px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-700"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCopyLogin}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition cursor-pointer"
                >
                  {loginCopied ? 'Copied' : 'Copy link'}
                </button>
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition cursor-pointer"
                >
                  Regenerate
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 px-3 py-2.5 rounded-lg border border-dashed border-gray-200 bg-gray-50 text-xs text-gray-500">
                The plaintext token is only shown right after regeneration. Click "Regenerate" to mint a fresh link to share.
              </div>
              <button
                type="button"
                onClick={handleRegenerate}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition cursor-pointer"
              >
                Regenerate
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Bio slide</h2>
            <Link
              href={`/superadmin/instructors/${instructor.id}/edit`}
              className="ml-auto text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              {instructor.bio_slide_attached ? 'Replace' : 'Upload'}
            </Link>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Single-page PDF shown as the first slide on every webinar this instructor hosts.
          </p>
          {!instructor.bio_slide_attached && (
            <p className="text-sm text-gray-500 py-6 text-center">No bio slide uploaded yet.</p>
          )}
          {instructor.bio_slide_attached && !instructor.bio_slide_ready && (
            <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Processing bio slide… Refresh in a few seconds.
            </div>
          )}
          {instructor.bio_slide_ready && instructor.bio_slide_image_url && (
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-900" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={instructor.bio_slide_image_url}
                alt="Instructor bio slide preview"
                className="w-full h-full object-contain"
                draggable={false}
              />
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assigned webinars</h2>
            <span className="ml-auto text-sm text-gray-400">
              {assignments.length} {assignments.length === 1 ? 'assignment' : 'assignments'}
            </span>
          </div>

          {assignments.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No assignments yet. Pick a webinar below.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {assignments.map((a) => (
                <div key={a.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900 truncate">{a.webinar.title}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${broadcastBadge[a.webinar.broadcast_state] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                        {a.webinar.broadcast_state}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Starts {fmtDate(a.webinar.starts_at)} · Assigned {a.assigned_at}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRemove(a)}
                      disabled={submittingId === a.id}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-red-700 bg-white hover:bg-red-50 border border-red-200 transition cursor-pointer disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assign a webinar</h2>
            <span className="ml-auto text-sm text-gray-400">
              {available_webinars.length} available
            </span>
          </div>

          <div className="relative mb-4">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search webinars by title, status, or delivery mode…"
              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {filteredWebinars.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {available_webinars.length === 0
                ? 'No unassigned webinars available. Every webinar is already assigned to an instructor.'
                : 'No webinars match that search.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto pr-3">
              {filteredWebinars.map((w) => (
                <div key={w.id} className="py-3 pl-1 flex items-center justify-between gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{w.title}</p>
                    <p className="text-xs text-gray-500">
                      {fmtDate(w.starts_at)} · {w.status} · {w.delivery_mode}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAssign(w)}
                    disabled={assigningWebinarId === w.id}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition cursor-pointer disabled:opacity-50"
                  >
                    {assigningWebinarId === w.id ? 'Assigning…' : 'Assign'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
